import {
    BrowserRouter,
    Routes,
    Route,
    Navigate
} from "react-router-dom";

import {
    useEffect,
    useState
} from "react";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import ImportPage from "./pages/ImportPage";
import ExportPage from "./pages/ExportPage";
import Staff from "./pages/Staff";
import History from "./pages/History";
import More from "./pages/More";
import SuperAdmin from "./pages/SuperAdmin";

import api from "./services/api";

import "./App.css";


function App() {

    const [token, setToken] = useState(
        localStorage.getItem("token")
    );

    const [currentUser, setCurrentUser] =
        useState(null);

    const [permissions, setPermissions] =
        useState([]);

    const [accessLoading, setAccessLoading] =
        useState(Boolean(token));


    // ========================================
    // LOAD CURRENT USER + PERMISSIONS
    // ========================================

    const loadAccess = async () => {

        if (!token) {

            setCurrentUser(null);
            setPermissions([]);
            setAccessLoading(false);

            return;
        }


        try {

            setAccessLoading(true);


            const response =
                await api.get("/auth/me");


            setCurrentUser(
                response.data.user
            );


            setPermissions(
                response.data.permissions || []
            );


            localStorage.setItem(
                "user",
                JSON.stringify(
                    response.data.user
                )
            );


        } catch (error) {

            console.error(
                "Unable to load account access",
                error
            );


            localStorage.removeItem("token");
            localStorage.removeItem("user");


            setToken(null);
            setCurrentUser(null);
            setPermissions([]);


        } finally {

            setAccessLoading(false);
        }
    };


    useEffect(() => {

        loadAccess();

    }, [token]);


    // ========================================
    // LOGIN
    // ========================================

    const handleLogin = (
        newToken
    ) => {

        localStorage.setItem(
            "token",
            newToken
        );

        setToken(newToken);
    };


    // ========================================
    // LOGOUT
    // ========================================

    const handleLogout = () => {

        localStorage.removeItem("token");
        localStorage.removeItem("user");

        setToken(null);
        setCurrentUser(null);
        setPermissions([]);
    };


    // ========================================
    // ROLE HELPERS
    // ========================================

    const isSuperAdmin =
        currentUser?.role ===
        "SUPER_ADMIN";


    const isOwner =
        currentUser?.role ===
        "OWNER";


    // ========================================
    // PERMISSION HELPER
    // ========================================

    const hasPermission = (
        permissionKey
    ) => {

        if (isOwner) {
            return true;
        }


        if (isSuperAdmin) {
            return false;
        }


        return permissions.some(
            permission =>
                permission.permission_key ===
                permissionKey
        );
    };


    // ========================================
    // WHERE SHOULD THIS USER GO?
    // ========================================

    const homePath =
        isSuperAdmin
            ? "/super-admin"
            : "/dashboard";


    // ========================================
    // BUSINESS USER PAGE
    // OWNER + STAFF ONLY
    // ========================================

    const businessPage = (
        component
    ) => {

        if (!token) {

            return (
                <Navigate
                    to="/login"
                    replace
                />
            );
        }


        if (isSuperAdmin) {

            return (
                <Navigate
                    to="/super-admin"
                    replace
                />
            );
        }


        return component;
    };


    // ========================================
    // PERMISSION-PROTECTED BUSINESS PAGE
    // ========================================

    const permissionPage = (
        component,
        permissionKey
    ) => {

        if (!token) {

            return (
                <Navigate
                    to="/login"
                    replace
                />
            );
        }


        if (isSuperAdmin) {

            return (
                <Navigate
                    to="/super-admin"
                    replace
                />
            );
        }


        if (
            !hasPermission(
                permissionKey
            )
        ) {

            return (
                <Navigate
                    to="/dashboard"
                    replace
                />
            );
        }


        return component;
    };


    // ========================================
    // OWNER-ONLY BUSINESS PAGE
    // ========================================

    const ownerPage = (
        component
    ) => {

        if (!token) {

            return (
                <Navigate
                    to="/login"
                    replace
                />
            );
        }


        if (isSuperAdmin) {

            return (
                <Navigate
                    to="/super-admin"
                    replace
                />
            );
        }


        if (!isOwner) {

            return (
                <Navigate
                    to="/dashboard"
                    replace
                />
            );
        }


        return component;
    };


    // ========================================
    // SUPER ADMIN ONLY PAGE
    // ========================================

    const superAdminPage = (
        component
    ) => {

        if (!token) {

            return (
                <Navigate
                    to="/login"
                    replace
                />
            );
        }


        if (!isSuperAdmin) {

            return (
                <Navigate
                    to="/dashboard"
                    replace
                />
            );
        }


        return component;
    };


    // ========================================
    // WAIT FOR ACCESS TO LOAD
    // ========================================

    if (
        token &&
        accessLoading
    ) {

        return (

            <div className="mobile-app">

                <div className="loading-state">
                    Loading...
                </div>

            </div>
        );
    }


    // ========================================
    // ROUTES
    // ========================================

    return (

        <BrowserRouter>

            <Routes>


                {/* LOGIN */}

                <Route
                    path="/login"
                    element={
                        token
                            ? (
                                <Navigate
                                    to={
                                        homePath
                                    }
                                    replace
                                />
                            )
                            : (
                                <Login
                                    onLogin={
                                        handleLogin
                                    }
                                />
                            )
                    }
                />


                {/* SUPER ADMIN */}

                <Route
                    path="/super-admin"
                    element={
                        superAdminPage(
                            <SuperAdmin
                                onLogout={
                                    handleLogout
                                }
                            />
                        )
                    }
                />


                {/* DASHBOARD */}

                <Route
                    path="/dashboard"
                    element={
                        businessPage(
                            <Dashboard
                                onLogout={
                                    handleLogout
                                }
                            />
                        )
                    }
                />


                {/* PRODUCTS */}

                <Route
                    path="/products"
                    element={
                        businessPage(
                            <Products />
                        )
                    }
                />


                {/* IMPORT */}

                <Route
                    path="/import"
                    element={
                        permissionPage(
                            <ImportPage />,
                            "UPLOAD_EXCEL"
                        )
                    }
                />


                {/* EXPORT */}

                <Route
                    path="/export"
                    element={
                        permissionPage(
                            <ExportPage />,
                            "EXPORT_FILES"
                        )
                    }
                />


                {/* STAFF - OWNER ONLY */}

                <Route
                    path="/staff"
                    element={
                        ownerPage(
                            <Staff />
                        )
                    }
                />


                {/* HISTORY */}

                <Route
                    path="/history"
                    element={
                        permissionPage(
                            <History />,
                            "VIEW_PRICE_HISTORY"
                        )
                    }
                />


                {/* MORE */}

                <Route
                    path="/more"
                    element={
                        businessPage(
                            <More />
                        )
                    }
                />


                {/* ROOT */}

                <Route
                    path="/"
                    element={
                        <Navigate
                            to={
                                token
                                    ? homePath
                                    : "/login"
                            }
                            replace
                        />
                    }
                />


                {/* UNKNOWN URL */}

                <Route
                    path="*"
                    element={
                        <Navigate
                            to={
                                token
                                    ? homePath
                                    : "/login"
                            }
                            replace
                        />
                    }
                />


            </Routes>

        </BrowserRouter>
    );
}


export default App;